import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { CHARACTERS_MAP, SILHOUETTES_MAP } from "../maps";
import { CHARACTERS, SILHOUETTES } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";

/**
 * Silhouette guessing mode — 5 guesses to identify a character from a blurred image.
 * The silhouette image is revealed fully on game end (win or loss).
 * Progressive hint reveals the debut game after 3 wrong guesses.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, storedGuesses: Array<string>, setStoredGuesses: (g: Array<string>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<string>} props.storedGuesses - Previously submitted character name guesses.
 * @param {(g: Array<string>) => void} props.setStoredGuesses - Guess state setter.
 * @returns {JSX.Element} The silhouette game UI.
 */
export default function SilhouetteMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  storedGuesses,
  setStoredGuesses,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const guessedCharacters = storedGuesses || [];
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-silhouette",
    []
  );
  const [isCopied, setIsCopied] = useState(false);

  const [targetSilhouette] = useState(
    getDailyItem(SILHOUETTES, targetDateObj, SILHOUETTES_MAP)
  );

  const MAX_GUESSES = 5;
  const wrongGuessesCount = guessedCharacters.filter(
    (guess) => guess !== targetSilhouette.character
  ).length;
  const hasWon = guessedCharacters.includes(targetSilhouette.character);
  const guessesLeft = MAX_GUESSES - guessedCharacters.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  /** Processes a character guess — checks win, updates stats, adds to collection. */
  const handleGuess = (selectedCharacter) => {
    if (hasWon || isGameOver) return;
    if (guessedCharacters.includes(selectedCharacter.name)) return;

    const newGuesses = [...guessedCharacters, selectedCharacter.name];
    const isWin = selectedCharacter.name === targetSilhouette.character;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      if (typeof updateGameStats === "function") {
        updateGameStats("silhouette", isWin, newGuesses.length, MAX_GUESSES);
      }
    }

    if (isWin) {
      const selectedId = String(targetSilhouette.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection([...collection, selectedId]);
      }
    }

    setStoredGuesses(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-silhouette-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));
    }
  }, [hasWon, targetDateStr]);

  const [globalStats, setGlobalStats] = useState(null);

  const [isGameRevealed, setIsGameRevealed] = useState(false);
  const guessesNeededForGame = Math.max(0, 3 - wrongGuessesCount);

  // Dynamic Image Paths
  const silhouetteUrl = `/images/silhouettes/${targetSilhouette.id}.webp`;
  const showAnswerImage = hasWon || isGameOver;

  /** Filters characters by name, excluding already-guessed ones. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const filteredChars = CHARACTERS.filter((char) =>
        char.name.toLowerCase().includes(query) &&
        !guessedCharacters.includes(char.name)
      ).sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        const aStartsWith = nameA.startsWith(query);
        const bStartsWith = nameB.startsWith(query);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return nameA.localeCompare(nameB);
      });
      setSuggestions(filteredChars.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  const syncInProgress = useRef(false);

  // --- SILHOUETTE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-silhouette-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "silhouette_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedCharacters.length)
              : increment(0),
            guessTally: {},
          };
          guessedCharacters.forEach((char) => {
            if (char) updateData.guessTally[char] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedCharacters.length, targetDateStr]);

  /** Copies silhouette-mode results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedCharacters.length : "X";
    let shareText = `Kisekidle - Silhouette (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedCharacters
        .map((guess) => (guess === targetSilhouette.character ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedCharacters.forEach((guess, i) => {
        shareText += `${i + 1}. ${guess} ${guess === targetSilhouette.character ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal silhouette-terminal">
      <div className="header-container">
        <h2 className="mode-title">Guess the Silhouette</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      <div className="silhouette-box">
        <img
          src={silhouetteUrl}
          alt="Mystery Character"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          className={`silhouette-img ${showAnswerImage ? "revealed" : "hidden"}`}
        />
      </div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            It was <strong>{targetSilhouette.character}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The character was <strong>{targetSilhouette.character}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      <div className="hints-container">
        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isGameRevealed || hasWon || isGameOver
              ? "revealed"
              : guessesNeededForGame <= 0
                ? "unlocked"
                : "locked"
              }`}
            onClick={() => guessesNeededForGame <= 0 && setIsGameRevealed(true)}
            disabled={
              guessesNeededForGame > 0 || isGameRevealed || hasWon || isGameOver
            }
          >
            {isGameRevealed || hasWon || isGameOver
              ? `Debut: ${targetSilhouette.debutGame}`
              : "Reveal Debut Game"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForGame > 0
                ? `Available after ${guessesNeededForGame} more wrong guess${guessesNeededForGame > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>
      </div>

      {!hasWon && !isGameOver && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search for a character..."
            autoFocus
          />
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((c, index) => (
                <li
                  key={c.id}
                  className={index === focusedIndex ? "focused" : ""}
                  onClick={() => handleGuess(c)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  {c.imageUrl && (
                    <img
                      src={c.imageUrl}
                      alt={c.name}
                      loading="lazy"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span>{c.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ul className="guess-list">
        {guessedCharacters
          .slice()
          .reverse()
          .map((guess, index) => {
            const isCorrect = guess === targetSilhouette.character;
            const guessedCharacter = CHARACTERS.find((char) => char.name === guess);
            return (
              <li
                key={index}
                className={`guess-item ${isCorrect ? "correct" : "incorrect"}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {guessedCharacter?.imageUrl && (
                    <img
                      src={guessedCharacter.imageUrl}
                      alt={guess}
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <strong>{guess}</strong>
                </div>
                <span>{isCorrect ? "🎉 Correct!" : "❌ Incorrect"}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}